import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { paths } from '@/app/router/paths';
import { Avatar, Button, Field, Input, MonoLabel } from '@/design-system';
import { authApi, authProblem, isRevokedDevice, type InvitationPreview } from '@/entities/auth';
import { isApiError, problemKey, ROLE_LABELS, useSessionStore } from '@/shared/api';
import { formatDayLong, WEBSITE_SIGNUP_URL } from '@/shared/lib';

import {
    AuthError,
    AuthExternalLink,
    AuthHeading,
    AuthLayout,
    AuthLink,
    AuthNotice,
} from './AuthLayout';
import { authCopy } from './copy';
import { logout } from './session-lifecycle';
import { validateNewPassword } from './validation';

interface Errors {
    name?: string;
    password?: string;
    passwordConfirmation?: string;
}

type Outcome =
    | { kind: 'expired' | 'accepted' | 'not-found' | 'mismatch' }
    | { kind: 'disabled'; detail: string | null }
    | null;

/**
 * Welke mail de link bracht: een uitnodiging van iemand anders, of de activatie
 * na een eigen aanmelding op de website.
 */
export type InvitationVariant = 'invitation' | 'activation';

/** De zinnen die per variant verschillen; de rest van het scherm is hetzelfde. */
interface VariantText {
    eyebrow: string;
    loading: string;
    titleUnknown: string;
    title: (tenant: string) => string;
    body: (role: string, tenant: string) => string;
    accept: string;
    acceptBusy: string;
    notFound: string;
    expired: string;
    accepted: string;
    failed: string;
    loadFailed: string;
    /** Waar een link die niet meer werkt een nieuwe haalt, buiten de app; of niets. */
    renew: { href: string; label: string } | null;
}

const VARIANT_TEXT: Record<InvitationVariant, VariantText> = {
    invitation: {
        eyebrow: authCopy.invitationEyebrow,
        loading: authCopy.invitationLoading,
        titleUnknown: authCopy.invitationTitleUnknown,
        title: authCopy.invitationTitle,
        body: authCopy.invitationRole,
        accept: authCopy.invitationAccept,
        acceptBusy: authCopy.invitationAcceptBusy,
        notFound: authCopy.invitationNotFound,
        expired: authCopy.invitationExpired,
        accepted: authCopy.invitationAccepted,
        failed: authCopy.invitationFailed,
        loadFailed: authCopy.invitationLoadFailed,
        renew: null,
    },
    activation: {
        eyebrow: authCopy.activationEyebrow,
        loading: authCopy.activationLoading,
        titleUnknown: authCopy.activationTitleUnknown,
        title: () => authCopy.activationTitle,
        body: (_role, tenant) => authCopy.activationBody(tenant),
        accept: authCopy.activationAccept,
        acceptBusy: authCopy.activationAcceptBusy,
        notFound: authCopy.activationNotFound,
        expired: authCopy.activationExpired,
        accepted: authCopy.activationAccepted,
        failed: authCopy.activationFailed,
        loadFailed: authCopy.activationLoadFailed,
        renew: { href: WEBSITE_SIGNUP_URL, label: authCopy.activationSignupAgain },
    },
};

/**
 * Een uitnodiging accepteren: `/uitnodiging/:token`, de link uit de mail.
 *
 * Het scherm vraagt eerst aan de API wat voor uitnodiging het is. Een nieuw
 * adres kiest een wachtwoord; wie al een Smartkeur-account heeft, accepteert
 * alleen - zijn wachtwoord blijft hetzelfde. Accepteren is meteen inloggen, in
 * de omgeving waarvoor je bent uitgenodigd.
 *
 * `variant="activation"` is hetzelfde scherm op `/activeren/:token`, voor wie
 * zich op de website aanmeldde: "Welkom bij Smartkeur, kies je wachtwoord", met
 * de naam die hij daar typte al ingevuld. Achter beide zit dezelfde uitnodiging.
 */
export function InvitationPage({ variant = 'invitation' }: { variant?: InvitationVariant }) {
    const { token = '' } = useParams();
    const text = VARIANT_TEXT[variant];

    /* Sleutel begint met `public`: dit komt niet in de bewaarde querycache. */
    const preview = useQuery({
        queryKey: ['publicInvitation', token],
        queryFn: () => authApi.invitation(token),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });

    if (preview.isPending) {
        return (
            <AuthLayout>
                <AuthHeading eyebrow={text.eyebrow} title={text.loading} />
            </AuthLayout>
        );
    }

    if (preview.isError) {
        const problem = authProblem(preview.error);

        if (problem === 'not-found') {
            return <Closed text={text} body={text.notFound} renew />;
        }

        return (
            <AuthLayout>
                <AuthHeading eyebrow={text.eyebrow} title={text.titleUnknown} />
                <AuthError tone={problem === 'offline' ? 'offline' : 'danger'}>
                    {problem === 'offline' ? authCopy.offline : text.loadFailed}
                </AuthError>
                <Button
                    variant="outline"
                    size="lg"
                    fullWidth
                    onClick={() => void preview.refetch()}
                >
                    {authCopy.retry}
                </Button>
            </AuthLayout>
        );
    }

    if (preview.data.isAccepted) {
        return <Closed text={text} body={text.accepted} email={preview.data.email} />;
    }

    if (preview.data.isExpired) {
        return <Closed text={text} body={text.expired} renew />;
    }

    return <AcceptOrMismatch token={token} invitation={preview.data} text={text} />;
}

/**
 * Een uitnodiging hoort bij het uitgenodigde adres. Ben je op dit apparaat
 * ingelogd met een ander account, dan accepteer je hem niet: je logt eerst uit en
 * opent de link opnieuw.
 */
function AcceptOrMismatch({
    token,
    invitation,
    text,
}: {
    token: string;
    invitation: InvitationPreview;
    text: VariantText;
}) {
    const signedInEmail = useSessionStore((state) => state.session?.user.email ?? null);

    if (signedInEmail && !sameAddress(signedInEmail, invitation.email)) {
        return (
            <Mismatch
                token={token}
                invited={invitation.email}
                signedIn={signedInEmail}
                text={text}
            />
        );
    }

    return <AcceptForm token={token} invitation={invitation} text={text} />;
}

function sameAddress(left: string, right: string): boolean {
    return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function Mismatch({
    token,
    invited,
    signedIn,
    text,
}: {
    token: string;
    invited: string;
    signedIn: string | null;
    text: VariantText;
}) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [busy, setBusy] = useState(false);

    return (
        <AuthLayout>
            <AuthHeading
                eyebrow={text.eyebrow}
                title={authCopy.invitationMismatchTitle}
                body={authCopy.invitationMismatchBody}
            />

            <dl className="m-0 grid gap-3 rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-[14px] py-3">
                <div className="flex flex-col gap-[3px]">
                    <dt>
                        <MonoLabel size={10}>{authCopy.invitationInvited}</MonoLabel>
                    </dt>
                    <dd className="m-0 text-[15px] leading-[1.4] break-all text-[var(--text-primary)]">
                        {invited}
                    </dd>
                </div>
                {signedIn ? (
                    <div className="flex flex-col gap-[3px]">
                        <dt>
                            <MonoLabel size={10}>{authCopy.invitationSignedInAs}</MonoLabel>
                        </dt>
                        <dd className="m-0 text-[15px] leading-[1.4] break-all text-[var(--text-primary)]">
                            {signedIn}
                        </dd>
                    </div>
                ) : null}
            </dl>

            <Button
                variant="primary"
                size="lg"
                fullWidth
                disabled={busy}
                onClick={() => {
                    setBusy(true);
                    /* Terug naar dezelfde link: een uitnodiging of een activatie. */
                    void logout().then(() =>
                        navigate(pathname || paths.invitation(token), { replace: true }),
                    );
                }}
            >
                {busy ? authCopy.invitationSigningOut : authCopy.invitationSignOut}
            </Button>
        </AuthLayout>
    );
}

function AcceptForm({
    token,
    invitation,
    text,
}: {
    token: string;
    invitation: InvitationPreview;
    text: VariantText;
}) {
    const navigate = useNavigate();
    const signIn = useSessionStore((state) => state.signIn);

    /* Na een aanmelding op de website staat de naam van daar al klaar. */
    const [name, setName] = useState(invitation.name ?? '');
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [errors, setErrors] = useState<Errors>({});
    const [formError, setFormError] = useState<{ message: string; offline: boolean } | null>(null);
    const [outcome, setOutcome] = useState<Outcome>(null);
    const [busy, setBusy] = useState(false);

    const tenantName = invitation.tenant?.name ?? null;
    const roleLabel = invitation.role ? ROLE_LABELS[invitation.role] : invitation.roleLabel;

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();

        const next: Errors = invitation.requiresPassword
            ? validateNewPassword(password, confirmation)
            : {};

        setErrors(next);
        setFormError(null);

        if (next.password || next.passwordConfirmation) {
            return;
        }

        setBusy(true);

        try {
            const previous = useSessionStore.getState().session;
            const session = await authApi.acceptInvitation(token, {
                email: invitation.email,
                ...(invitation.requiresPassword
                    ? { name, password, passwordConfirmation: confirmation }
                    : {}),
            });

            /* Was hier iemand anders ingelogd, dan trekken we diens token in. */
            if (previous && previous.user.uuid !== session.user.uuid) {
                void authApi.logout(previous).catch(() => undefined);
            }

            await signIn(session);
            void navigate(paths.dashboard, { replace: true });
        } catch (caught) {
            setBusy(false);

            const problem = authProblem(caught);

            const key = problemKey(caught);

            if (key === 'invitation-email-mismatch') {
                setOutcome({ kind: 'mismatch' });
            } else if (key === 'invitation-account-disabled') {
                setOutcome({
                    kind: 'disabled',
                    detail: isApiError(caught) ? (caught.problem.detail ?? null) : null,
                });
            } else if (problem === 'invitation-expired') {
                setOutcome({ kind: 'expired' });
            } else if (problem === 'invitation-already-accepted') {
                setOutcome({ kind: 'accepted' });
            } else if (problem === 'not-found') {
                setOutcome({ kind: 'not-found' });
            } else if (problem === 'validation' && isApiError(caught)) {
                const fields: Errors = {
                    ...optional('name', caught.firstValidationError('name')),
                    ...optional('password', caught.firstValidationError('password')),
                    ...optional(
                        'passwordConfirmation',
                        caught.firstValidationError('password_confirmation'),
                    ),
                };

                setErrors(fields);

                if (!fields.name && !fields.password && !fields.passwordConfirmation) {
                    setFormError({ message: text.failed, offline: false });
                }
            } else {
                setFormError({
                    message:
                        problem === 'offline'
                            ? authCopy.offline
                            : problem === 'device-unavailable'
                              ? isRevokedDevice(caught)
                                  ? authCopy.deviceRevoked
                                  : authCopy.deviceUnavailable
                              : text.failed,
                    offline: problem === 'offline',
                });
            }
        }
    }

    if (outcome?.kind === 'expired') {
        return <Closed text={text} body={text.expired} renew />;
    }

    if (outcome?.kind === 'accepted') {
        return <Closed text={text} body={text.accepted} email={invitation.email} />;
    }

    if (outcome?.kind === 'not-found') {
        return <Closed text={text} body={text.notFound} renew />;
    }

    if (outcome?.kind === 'mismatch') {
        return (
            <Mismatch
                token={token}
                invited={invitation.email}
                signedIn={useSessionStore.getState().session?.user.email ?? null}
                text={text}
            />
        );
    }

    if (outcome?.kind === 'disabled') {
        return (
            <Closed
                text={text}
                title={authCopy.invitationDisabledTitle}
                body={outcome.detail ?? authCopy.invitationDisabled}
            />
        );
    }

    return (
        <AuthLayout>
            <AuthHeading
                eyebrow={text.eyebrow}
                title={tenantName ? text.title(tenantName) : text.titleUnknown}
                body={tenantName ? text.body(roleLabel.toLowerCase(), tenantName) : undefined}
            />

            <div className="flex items-center gap-3 rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-[14px] py-3">
                {invitation.tenant?.logoUrl ? (
                    <img
                        src={invitation.tenant.logoUrl}
                        alt=""
                        className="h-[34px] w-[34px] shrink-0 rounded-full border border-[var(--placeholder-border)] object-contain"
                    />
                ) : (
                    <Avatar size={34} variant="placeholder" />
                )}
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] leading-[1.3] font-medium text-[var(--text-primary)]">
                        {tenantName ?? invitation.email}
                    </span>
                    <span className="mt-[2px] block truncate text-[12px] leading-[1.35] text-[var(--text-tertiary-safe)]">
                        {invitation.invitedBy
                            ? `${roleLabel} · ${authCopy.invitationBy(invitation.invitedBy)}`
                            : roleLabel}
                    </span>
                </span>
                <MonoLabel size={10}>
                    {authCopy.invitationValidUntil(formatDayLong(invitation.expiresAt))}
                </MonoLabel>
            </div>

            <form noValidate className="flex flex-col gap-3" onSubmit={handleSubmit}>
                <div className="flex flex-col gap-[5px]">
                    <MonoLabel size={10}>{authCopy.email}</MonoLabel>
                    <span className="text-[15px] leading-[1.4] break-all text-[var(--text-primary)]">
                        {invitation.email}
                    </span>
                </div>

                {invitation.requiresPassword ? (
                    <>
                        <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                            {authCopy.invitationNewAccount}
                        </p>

                        <Field
                            label={authCopy.invitationName}
                            labelCase="caps"
                            size="lg"
                            description={authCopy.invitationNameHint}
                            error={errors.name}
                        >
                            <Input
                                autoComplete="name"
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                            />
                        </Field>

                        <Field
                            label={authCopy.newPassword}
                            labelCase="caps"
                            size="lg"
                            description={authCopy.resetBody}
                            error={errors.password}
                        >
                            <Input
                                type="password"
                                autoComplete="new-password"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                            />
                        </Field>

                        <Field
                            label={authCopy.confirmPassword}
                            labelCase="caps"
                            size="lg"
                            error={errors.passwordConfirmation}
                        >
                            <Input
                                type="password"
                                autoComplete="new-password"
                                value={confirmation}
                                onChange={(event) => setConfirmation(event.target.value)}
                            />
                        </Field>
                    </>
                ) : (
                    <AuthNotice>{authCopy.invitationExistingAccount}</AuthNotice>
                )}

                {formError ? (
                    <AuthError tone={formError.offline ? 'offline' : 'danger'}>
                        {formError.message}
                    </AuthError>
                ) : null}

                <Button type="submit" variant="primary" size="lg" fullWidth disabled={busy}>
                    {busy ? text.acceptBusy : text.accept}
                </Button>
            </form>
        </AuthLayout>
    );
}

/**
 * Een uitnodiging waar niets (meer) mee kan. `renew`: de link werkt niet meer, en
 * de variant kent een plek waar je een nieuwe haalt (een activatie: opnieuw
 * aanmelden op de website).
 */
function Closed({
    text,
    body,
    email,
    title,
    renew = false,
}: {
    text: VariantText;
    body: string;
    email?: string;
    title?: string;
    renew?: boolean;
}) {
    return (
        <AuthLayout>
            <AuthHeading eyebrow={text.eyebrow} title={title ?? text.titleUnknown} body={body} />
            <div className="flex flex-wrap justify-start gap-x-5">
                <AuthLink
                    to={email ? `${paths.login}?email=${encodeURIComponent(email)}` : paths.login}
                >
                    {authCopy.goToLogin}
                </AuthLink>
                {renew && text.renew ? (
                    <AuthExternalLink href={text.renew.href}>{text.renew.label}</AuthExternalLink>
                ) : null}
            </div>
        </AuthLayout>
    );
}

function optional<K extends keyof Errors>(key: K, value: string | undefined): Errors {
    return value ? { [key]: value } : {};
}
